import React from 'react';

/**
 * Live checklist for the local-account password policy.
 *
 * The backend (backend/utils/password-policy.js) is the enforcement point; the
 * tests below only drive the checkmarks as the user types. Rule labels are
 * fetched from GET /users/password-policy where available so the wording shown
 * always matches what the API enforces — DEFAULT_PASSWORD_RULES is the fallback.
 */

export const PASSWORD_MIN_LENGTH = 12;

export const PASSWORD_RULE_TESTS = {
  length: pw => pw.length >= PASSWORD_MIN_LENGTH,
  upper: pw => /[A-Z]/.test(pw),
  lower: pw => /[a-z]/.test(pw),
  special: pw => /[^A-Za-z0-9]/.test(pw),
};

export const DEFAULT_PASSWORD_RULES = [
  { id: 'length', label: `At least ${PASSWORD_MIN_LENGTH} characters` },
  { id: 'upper', label: 'At least one uppercase letter (A-Z)' },
  { id: 'lower', label: 'At least one lowercase letter (a-z)' },
  { id: 'special', label: 'At least one special character (e.g. !@#$%^&*)' },
];

/** Rules the given password does not satisfy. */
export function passwordFailures(password, rules = DEFAULT_PASSWORD_RULES) {
  const pw = password || '';
  return rules.filter(r => {
    const test = PASSWORD_RULE_TESTS[r.id];
    return test ? !test(pw) : false;
  });
}

export default function PasswordPolicyHelp({ password = '', rules, style }) {
  const list = rules && rules.length ? rules : DEFAULT_PASSWORD_RULES;
  const touched = (password || '').length > 0;

  return (
    <div style={{
      marginTop: -8, marginBottom: 16, padding: '10px 14px',
      borderRadius: 'var(--radius)', border: '1px solid var(--border)',
      background: 'var(--bg-input)', fontSize: 12, ...style
    }}>
      <div style={{ fontWeight: 600, marginBottom: 6, color: 'var(--text-secondary)' }}>
        Password requirements
      </div>
      <ul style={{ listStyle: 'none', margin: 0, padding: 0, display: 'grid', gap: 3 }}>
        {list.map(rule => {
          const test = PASSWORD_RULE_TESTS[rule.id];
          const met = test ? test(password || '') : false;
          return (
            <li key={rule.id} style={{
              display: 'flex', alignItems: 'center', gap: 8,
              color: !touched ? 'var(--text-secondary)' : (met ? '#16a34a' : '#dc2626')
            }}>
              <span aria-hidden="true" style={{ width: 12, textAlign: 'center' }}>
                {!touched ? '•' : (met ? '✓' : '✗')}
              </span>
              <span>{rule.label}</span>
            </li>
          );
        })}
      </ul>
      <div style={{ marginTop: 6, color: 'var(--text-secondary)' }}>
        Applies to local accounts. SSO users sign in through their identity provider.
      </div>
    </div>
  );
}
